import React from "react";
import { Sheet, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Shield, ExternalLink, Calendar, MapPin, XIcon, Plane, Copy, AlertTriangle } from "lucide-react";
import { useStore } from "@/store/store";
import { cn } from "@/lib/utils";
import { FeatureFlags } from "@/feature_flags/feature_flag";
import { useFlag } from "@openfeature/react-sdk";
import { ZoneNotes } from "@/components/flight-restriction-zones/zone-notes";
import type { ZoneNote } from "@/api/v1/flight-restriction-zone-types";
import { apiTypeToLabel } from "@/lib/zone-type-utils";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

// Custom SheetContent with semi-transparent overlay
const CustomSheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "top" | "right" | "bottom" | "left"
  }
>(({ className, children, side = "right", ...props }, ref) => (
  <SheetPrimitive.Portal>
    {/* Custom semi-transparent overlay */}
    <SheetPrimitive.Overlay
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/20",
      )}
    />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
        side === "right" &&
          "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
        side === "left" &&
          "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
        side === "top" &&
          "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
        side === "bottom" &&
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
        className
      )}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
        <XIcon className="size-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPrimitive.Portal>
));
CustomSheetContent.displayName = SheetPrimitive.Content.displayName;

export const ZoneDrawer: React.FC = () => {
  const { selectedZone, zoneDrawerOpen, setZoneDrawerOpen } = useStore();
  const { value: isFlightPlanSubmissionEnabled } = useFlag(FeatureFlags.flight_plan_submission_enabled, false);

  const handleSubmitFlightPlan = () => {
    // TODO: Implement flight plan submission logic
  };

  if (!selectedZone) return null;

  const getZoneIcon = (type: string) => {
    const typeLabel = apiTypeToLabel(type);
    switch (typeLabel) {
      case "Prohibited":
        return <Shield className="h-5 w-5 text-red-600 dark:text-red-400" />;
      case "Restricted":
        return <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      case "Danger":
        return <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />;
      default:
        return <MapPin className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getZoneColor = (type: string) => {
    const typeLabel = apiTypeToLabel(type);
    switch (typeLabel) {
      case "Prohibited":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800";
      case "Restricted":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800";
      case "Danger":
        return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
    }
  };

  // Extract notes from the zone
  const notes: ZoneNote[] | null = selectedZone.notes || null;
  
  // Calculate if zone is active based on effective times
  const now = new Date();
  const beginTime = new Date(selectedZone.effective_begin_time);
  const endTime = selectedZone.effective_end_time ? new Date(selectedZone.effective_end_time) : null;
  const isActive = beginTime <= now && (!endTime || endTime >= now);

  return (
    <Sheet open={zoneDrawerOpen} onOpenChange={setZoneDrawerOpen}>
      <CustomSheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <div className="flex items-center gap-3">
            {getZoneIcon(selectedZone.type)}
            <div className="flex-1">
              <SheetTitle className="text-xl">{selectedZone.name}</SheetTitle>
              <SheetDescription className="mt-1">
                <Badge className={getZoneColor(selectedZone.type)}>
                  {apiTypeToLabel(selectedZone.type)}
                </Badge>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
          {/* Zone Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Zone Information</h3>
            <div className="grid grid-cols-1 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-muted-foreground">Designator:</span>
                <span className="font-mono text-xs">{selectedZone.designator}</span>
              </div>
              {selectedZone.designator_icao && (
                <div className="flex justify-between">
                  <span className="font-medium text-muted-foreground">ICAO Designator:</span>
                  <span className="font-mono text-xs">{selectedZone.designator_icao}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="font-medium text-muted-foreground">Status:</span>
                <Badge variant={isActive ? "default" : "secondary"}>
                  {isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-muted-foreground">Upper Limit:</span>
                <span>{selectedZone.upper_limit} {selectedZone.upper_limit_uom}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-muted-foreground">Lower Limit:</span>
                <span>{selectedZone.lower_limit} {selectedZone.lower_limit_uom}</span>
              </div>
            </div>
          </div>

          {/* Effective Dates */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Effective Period</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-muted-foreground">Effective:</span>
                <span>{new Date(selectedZone.effective_begin_time).toLocaleDateString()}</span>
                {selectedZone.effective_end_time && (
                  <>
                    <span className="text-muted-foreground">to</span>
                    <span>{new Date(selectedZone.effective_end_time).toLocaleDateString()}</span>
                  </>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Valid time: {new Date(selectedZone.valid_time).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Notes */}
          {notes && notes.length > 0 && (
            <div className="space-y-4">
              <ZoneNotes notes={notes} />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="pt-4 border-t px-4 space-y-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            asChild
          >
            <Link 
              to="/flight-restriction-zones/$zoneSlug" 
              params={{ zoneSlug: selectedZone.designator }}
            >
              <ExternalLink className="h-4 w-4 mr-2 shrink-0" />
              View Details
            </Link>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={() => setZoneDrawerOpen(false)}
          >
            <XIcon className="h-4 w-4 mr-2 shrink-0" />
            Close
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={async () => {
              try {
                // Create URL to the map with this zone selected
                const url = new URL("/map", window.location.origin);
                url.searchParams.set("zone", selectedZone.designator);
                await navigator.clipboard.writeText(url.toString());
                toast.success("Link copied to clipboard");
              } catch {
                toast.error("Failed to copy link");
              }
            }}
          >
            <Copy className="h-4 w-4 mr-2 shrink-0" />
            Share Zone
          </Button>
          
          {/* Submit Flight Plan Button - Only shown when feature flag is enabled */}
          
            <Button
                onClick={handleSubmitFlightPlan}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-start"
                size="sm"
                disabled={!isFlightPlanSubmissionEnabled}
            >
                <Plane className="h-4 w-4 mr-2 shrink-0" />
                Submit Flight Plan
            </Button>

        </div>
      </CustomSheetContent>
    </Sheet>
  );
};
